import { Capacitor } from '@capacitor/core';
import {
  PushNotifications,
  Token,
  PushNotificationSchema,
  ActionPerformed,
  PermissionStatus,
} from '@capacitor/push-notifications';
import { client } from './amplifyClient';

/**
 * Push Notifications Service
 *
 * Handles native push notification registration and management for iOS/Android.
 * Uses FCM for Android and APNs for iOS.
 *
 * Device tokens are stored in DynamoDB via the Amplify DeviceToken model
 * for server-side push notification delivery.
 */

export interface DeviceTokenData {
  id?: string;
  token: string;
  platform: 'IOS' | 'ANDROID' | 'WEB';
  userId: string;
  isActive?: boolean;
  deviceModel?: string;
  osVersion?: string;
  appVersion?: string;
}

export interface PushNotificationHandler {
  onRegistration?: (token: Token) => void;
  onRegistrationError?: (error: Error) => void;
  onPushNotificationReceived?: (notification: PushNotificationSchema) => void;
  onPushNotificationActionPerformed?: (action: ActionPerformed) => void;
}

const isNative = Capacitor.isNativePlatform();

let isInitialized = false;
let registeredHandlers: PushNotificationHandler = {};

/**
 * Check if push notifications are available on this platform
 */
export function isPushNotificationsAvailable(): boolean {
  return isNative;
}

/**
 * Get current permission status for push notifications
 */
export async function checkPushPermissions(): Promise<PermissionStatus> {
  if (!isNative) {
    // Web - check using Notification API
    if ('Notification' in window) {
      const webPermission = Notification.permission;
      return {
        receive: webPermission === 'granted' ? 'granted' : webPermission === 'denied' ? 'denied' : 'prompt',
      };
    }
    return { receive: 'denied' };
  }

  return PushNotifications.checkPermissions();
}

/**
 * Request push notification permissions
 */
export async function requestPushPermissions(): Promise<PermissionStatus> {
  if (!isNative) {
    // Web fallback
    if ('Notification' in window) {
      const result = await Notification.requestPermission();
      return {
        receive: result === 'granted' ? 'granted' : result === 'denied' ? 'denied' : 'prompt',
      };
    }
    return { receive: 'denied' };
  }

  return PushNotifications.requestPermissions();
}

/**
 * Initialize push notifications with handlers
 */
export async function initializePushNotifications(
  handlers: PushNotificationHandler = {}
): Promise<void> {
  if (!isNative) {
    return;
  }

  if (isInitialized) {
    return;
  }

  registeredHandlers = handlers;

  // Listen for registration success
  await PushNotifications.addListener('registration', (token: Token) => {
    registeredHandlers.onRegistration?.(token);
  });

  // Listen for registration errors
  await PushNotifications.addListener('registrationError', (error) => {
    // [PushNotifications] Registration error (suppressed)
    registeredHandlers.onRegistrationError?.(new Error(error.error));
  });

  // Listen for push notifications received while app is in foreground
  await PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
    registeredHandlers.onPushNotificationReceived?.(notification);
  });

  // Listen for push notification actions (user tapped notification)
  await PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
    registeredHandlers.onPushNotificationActionPerformed?.(action);
  });

  isInitialized = true;
}

/**
 * Register device for push notifications
 * This triggers the permission request if not already granted
 */
export async function registerForPushNotifications(): Promise<string | null> {
  if (!isNative) {
    return null;
  }

  // Check current permissions
  let permStatus = await checkPushPermissions();

  if (permStatus.receive === 'prompt') {
    permStatus = await requestPushPermissions();
  }

  if (permStatus.receive !== 'granted') {
    return null;
  }

  // Register with APNs/FCM — token arrives via registration listener
  await PushNotifications.register();
  return null;
}

/**
 * Save device token to backend for a user
 *
 * Stores the push notification token in DynamoDB via Amplify.
 * If a token already exists, updates it; otherwise creates a new record.
 */
export async function saveDeviceToken(
  userId: string,
  token: string,
  deviceInfo?: { model?: string; osVersion?: string; appVersion?: string }
): Promise<void> {
  const rawPlatform = Capacitor.getPlatform();
  const platform = rawPlatform.toUpperCase() as 'IOS' | 'ANDROID' | 'WEB';

  try {
    // Check if this token already exists
    const { data: existingTokens } = await client.models.DeviceToken.list({
      filter: { token: { eq: token } },
    });

    if (existingTokens && existingTokens.length > 0) {
      // Update existing token
      const existing = existingTokens[0];
      await client.models.DeviceToken.update({
        id: existing.id,
        isActive: true,
        deviceModel: deviceInfo?.model,
        osVersion: deviceInfo?.osVersion,
        appVersion: deviceInfo?.appVersion,
      });
      // Updated existing device token
    } else {
      // Create new token
      await client.models.DeviceToken.create({
        userId,
        token,
        platform,
        isActive: true,
        deviceModel: deviceInfo?.model,
        osVersion: deviceInfo?.osVersion,
        appVersion: deviceInfo?.appVersion,
      });
      // Created new device token
    }

    // Also store locally for offline access
    localStorage.setItem('currentDeviceToken', token);
  } catch (error) {
    // [PushNotifications] Failed to save device token (suppressed)
    // Store locally as fallback
    localStorage.setItem('currentDeviceToken', token);
    localStorage.setItem('pendingDeviceToken', JSON.stringify({
      userId,
      token,
      platform,
      deviceModel: deviceInfo?.model,
      osVersion: deviceInfo?.osVersion,
      appVersion: deviceInfo?.appVersion,
    }));
    // Saved device token locally for later sync
  }
}

/**
 * Remove device token from backend (e.g., on logout)
 * Marks the token as inactive rather than deleting it.
 */
export async function removeDeviceToken(userId: string, token: string): Promise<void> {
  try {
    // Find the token in the backend
    const { data: existingTokens } = await client.models.DeviceToken.list({
      filter: {
        token: { eq: token },
        userId: { eq: userId },
      },
    });

    if (existingTokens && existingTokens.length > 0) {
      // Mark as inactive instead of deleting (allows re-activation)
      await client.models.DeviceToken.update({
        id: existingTokens[0].id,
        isActive: false,
      });
      // Deactivated device token
    }

    // Clear local storage
    localStorage.removeItem('currentDeviceToken');
    localStorage.removeItem('pendingDeviceToken');
  } catch (error) {
    // [PushNotifications] Failed to remove device token (suppressed)
    // Still clear local storage
    localStorage.removeItem('currentDeviceToken');
    localStorage.removeItem('pendingDeviceToken');
  }
}

/**
 * Sync any pending device tokens that failed to save
 */
export async function syncPendingDeviceToken(): Promise<void> {
  try {
    const pending = localStorage.getItem('pendingDeviceToken');
    if (!pending) return;

    const tokenData = JSON.parse(pending) as DeviceTokenData;
    await saveDeviceToken(tokenData.userId, tokenData.token, {
      model: tokenData.deviceModel,
      osVersion: tokenData.osVersion,
      appVersion: tokenData.appVersion,
    });

    localStorage.removeItem('pendingDeviceToken');
    // Synced pending device token
  } catch (error) {
    // [PushNotifications] Failed to sync pending token (suppressed)
  }
}

/**
 * Get all active device tokens for a user
 */
export async function getUserDeviceTokens(userId: string): Promise<DeviceTokenData[]> {
  try {
    const { data } = await client.models.DeviceToken.list({
      filter: {
        userId: { eq: userId },
        isActive: { eq: true },
      },
    });

    return (data || []).map((t) => ({
      id: t.id,
      token: t.token!,
      platform: t.platform as 'IOS' | 'ANDROID' | 'WEB',
      userId: t.userId!,
      isActive: t.isActive ?? true,
      deviceModel: t.deviceModel ?? undefined,
      osVersion: t.osVersion ?? undefined,
      appVersion: t.appVersion ?? undefined,
    }));
  } catch (error) {
    // [PushNotifications] Failed to get user device tokens (suppressed)
    return [];
  }
}

/**
 * Get all delivered notifications (iOS only)
 */
export async function getDeliveredNotifications(): Promise<PushNotificationSchema[]> {
  if (!isNative) {
    return [];
  }

  try {
    const result = await PushNotifications.getDeliveredNotifications();
    return result.notifications;
  } catch (error) {
    // [PushNotifications] Failed to get delivered notifications (suppressed)
    return [];
  }
}

/**
 * Remove delivered notifications
 */
export async function removeDeliveredNotifications(ids?: string[]): Promise<void> {
  if (!isNative) {
    return;
  }

  try {
    if (ids && ids.length > 0) {
      // Get current notifications to find matching ones
      const { notifications } = await PushNotifications.getDeliveredNotifications();
      const toRemove = notifications.filter((n) => ids.includes(n.id));
      if (toRemove.length > 0) {
        await PushNotifications.removeDeliveredNotifications({ notifications: toRemove });
      }
    } else {
      await PushNotifications.removeAllDeliveredNotifications();
    }
  } catch (error) {
    // [PushNotifications] Failed to remove notifications (suppressed)
  }
}

/**
 * Clean up push notification listeners
 */
export async function cleanupPushNotifications(): Promise<void> {
  if (!isNative) {
    return;
  }

  try {
    await PushNotifications.removeAllListeners();
    isInitialized = false;
    registeredHandlers = {};
    // Cleaned up listeners
  } catch (error) {
    // [PushNotifications] Failed to cleanup (suppressed)
  }
}

/**
 * Get the badge count (iOS only)
 */
export async function getBadgeCount(): Promise<number> {
  // Badge management is platform-specific and may need native implementation
  return 0;
}

/**
 * Set the badge count (iOS only)
 */
export async function setBadgeCount(_count: number): Promise<void> {
  // Badge management is platform-specific and may need native implementation
  // setBadgeCount not implemented
}
